import { z } from 'zod'
import {
  TRAINER_CONTRACT_VERSION,
  trainerReadSideEffectsSchema,
  trainerWriteSideEffectsSchema,
} from './trainer-event-read'

const nullableString = z.string().nullable()
const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const trainerSocialMediaReadQuerySchema = z.object({
  orgId: optionalQueryString,
  audience: optionalQueryString,
  platform: optionalQueryString,
  type: optionalQueryString,
  includeArchived: optionalQueryString,
})

export const trainerSocialMediaSourceStateSchema = z.object({
  kind: z.enum([
    'external_url',
    'missing',
    'invalid_url',
    'historical_preview_only',
    'provider_unavailable',
    'owned_upload_pending',
    'signed_provider_download',
  ]),
  label: z.string(),
  openAvailable: z.boolean(),
  signedDownloadAvailable: z.boolean(),
  providerCleanupAvailable: z.boolean(),
  reason: z.string(),
  rawProviderPayloadReturned: z.literal(false),
})

export const trainerSocialMediaUploadSchema = z.object({
  uploadId: z.string(),
  filename: z.string(),
  contentType: z.string(),
  fileSizeBytes: z.number().finite().nullable(),
  status: z.enum(['', 'issued', 'completed']),
  completedAt: z.string(),
})

export const trainerSocialMediaSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  createdByUserId: z.string(),
  name: z.string(),
  platform: z.string(),
  type: z.string(),
  description: nullableString,
  imageUrl: nullableString,
  sourceUrl: nullableString,
  editable: z.boolean(),
  active: z.boolean(),
  audience: z.enum(['for_me', 'for_everyone']),
  audienceSource: z.enum(['explicit_migrated', 'derived_from_creator']),
  accessType: z.string(),
  sourceState: trainerSocialMediaSourceStateSchema,
  upload: trainerSocialMediaUploadSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const trainerSocialMediaReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([z.literal('trainer_social_media')]),
  projection: z.literal('trainer-social-media-catalog'),
  audienceResolution: z.object({
    trustedSourceFields: z.tuple([z.literal('audience_scope'), z.literal('access_type')]),
    compatibilityFallbackField: z.literal('created_by_user_id'),
    explicitMigratedCount: z.number().int().nonnegative(),
    derivedCompatibilityCount: z.number().int().nonnegative(),
    rawPayloadReturned: z.literal(false),
  }),
  rawPayloadReturned: z.literal(false),
})

export const trainerSocialMediaListResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  socialMedia: z.array(trainerSocialMediaSummarySchema),
  socialEvidence: trainerSocialMediaReadEvidenceSchema,
  sideEffects: trainerReadSideEffectsSchema,
})

export const trainerSocialMediaDetailResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  socialMedia: trainerSocialMediaSummarySchema,
  sideEffects: trainerWriteSideEffectsSchema,
})

export type TrainerSocialMediaReadQuery = z.infer<typeof trainerSocialMediaReadQuerySchema>
export type TrainerSocialMediaSourceState = z.infer<typeof trainerSocialMediaSourceStateSchema>
export type TrainerSocialMediaUpload = z.infer<typeof trainerSocialMediaUploadSchema>
export type TrainerSocialMediaSummary = z.infer<typeof trainerSocialMediaSummarySchema>
export type TrainerSocialMediaReadEvidence = z.infer<typeof trainerSocialMediaReadEvidenceSchema>
export type TrainerSocialMediaListResponse = z.infer<typeof trainerSocialMediaListResponseSchema>
export type TrainerSocialMediaDetailResponse = z.infer<typeof trainerSocialMediaDetailResponseSchema>
